'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { formationsList } from '@/data/formations';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';

type ContactFormData = {
  nom: string;
  email: string;
  telephone: string;
  sujet: string;
  formation: string;
  message: string;
  acceptConditions: boolean;
  company?: string;
  csrfToken?: string;
};

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [formStartedAt] = useState(() => Date.now());

  const { 
    register, 
    handleSubmit, 
    reset,
    formState: { errors } 
  } = useForm<ContactFormData>();

  useEffect(() => {
    const fetchCsrf = async () => {
      try {
        const res = await fetch('/api/contact/csrf', {
          credentials: 'include',
        });
        if (res.ok) {
          const body = await res.json();
          setCsrfToken(body.csrfToken ?? '');
        } else {
          setError('Impossible de préparer le formulaire (CSRF).');
        }
      } catch {
        setError('Impossible de préparer le formulaire (réseau).');
      }
    };

    fetchCsrf();
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    if (!csrfToken) {
      setError('Le formulaire n est pas prêt. Rechargez la page.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const sourceUrl = typeof window !== 'undefined' ? window.location.href : '';

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          nom: data.nom,
          email: data.email,
          telephone: data.telephone,
          message: data.message,
          formation: data.formation,
          sujet: data.sujet,
          consent: data.acceptConditions,
          company: data.company || '',
          formStartedAt,
          sourceUrl,
          csrfToken,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Trop de tentatives. Réessayez dans quelques instants.');
        }
        if (response.status === 400) {
          throw new Error('Validation échouée. Merci de vérifier les champs.');
        }
        if (response.status === 403) {
          throw new Error('Soumission refusée pour sécurité. Merci de réessayer.');
        }
        throw new Error('Echec de l envoi du message');
      }

      setIsSuccess(true);
      trackEvent('lead_submit', {
        lead_type: 'contact',
        formation_slug: data.formation || 'non_specifiee',
      });
      reset();

      // Réinitialiser après quelques secondes
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Erreur lors de la soumission');
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de l\'envoi du formulaire. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm backdrop-blur">
      <p className="mb-4 text-sm text-white/70">
        Decrivez votre besoin (effectif, delai, contexte). Nous revenons vers vous sous 24 a 48h ouvrées.
      </p>

      {isSuccess ? (
        <Alert variant="success" className="mb-4" title="Message envoye avec succes !">
          Nous vous repondrons dans les plus brefs delais.
        </Alert>
      ) : null}
      
      {error ? (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      ) : null}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        {/* Champs techniques anti-spam */}
        <input
          type="text"
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          {...register('company')}
        />

        <div>
          <label htmlFor="nom" className="block text-sm font-medium text-white/80 mb-1">
            Nom complet <span className="text-red-600">*</span>
          </label>
          <Input
            id="nom"
            type="text"
            error={Boolean(errors.nom)}
            aria-invalid={Boolean(errors.nom)}
            aria-describedby={errors.nom ? 'nom-error' : undefined}
            {...register('nom', { required: 'Le nom est requis' })}
          />
          {errors.nom && (
            <p id="nom-error" className="mt-1 text-sm text-red-600">{errors.nom.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1">
            Email <span className="text-red-600">*</span>
          </label>
          <Input
            id="email"
            type="email"
            error={Boolean(errors.email)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            {...register('email', { 
              required: 'L\'email est requis',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Adresse email invalide'
              }
            })}
          />
          {errors.email && (
            <p id="email-error" className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="telephone" className="block text-sm font-medium text-white/80 mb-1">
            Téléphone
          </label>
          <Input
            id="telephone"
            type="tel"
            aria-describedby="telephone-help"
            {...register('telephone')}
          />
          <p id="telephone-help" className="mt-1 text-xs text-white/60">Optionnel. Utilise uniquement pour vous rappeler rapidement.</p>
        </div>
        
        <div>
          <label htmlFor="sujet" className="block text-sm font-medium text-white/80 mb-1">
            Sujet <span className="text-red-600">*</span>
          </label>
          <Select
            id="sujet"
            error={Boolean(errors.sujet)}
            aria-invalid={Boolean(errors.sujet)}
            aria-describedby={errors.sujet ? 'sujet-error' : undefined}
            {...register('sujet', { required: 'Le sujet est requis' })}
          >
            <option value="">Sélectionnez un sujet</option>
            <option value="devis">Demande de devis</option>
            <option value="renseignement">Renseignement sur une formation</option>
            <option value="intra">Formation en intra-entreprise</option>
            <option value="partenariat">Proposition de partenariat</option>
            <option value="autre">Autre demande</option>
          </Select>
          {errors.sujet && (
            <p id="sujet-error" className="mt-1 text-sm text-red-600">{errors.sujet.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="formation" className="block text-sm font-medium text-white/80 mb-1">
            Formation souhaitée <span className="text-red-600">*</span>
          </label>
          <Select
            id="formation"
            error={Boolean(errors.formation)}
            aria-invalid={Boolean(errors.formation)}
            aria-describedby={errors.formation ? 'formation-error' : undefined}
            {...register('formation', { required: 'Veuillez sélectionner une formation' })}
          >
            <option value="">Sélectionnez une formation</option>
            {formationsList.map((formation) => (
              <option key={formation.id} value={formation.slug}>
                {formation.title}
              </option>
            ))}
          </Select>
          {errors.formation && (
            <p id="formation-error" className="mt-1 text-sm text-red-600">{errors.formation.message}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-1">
            Message <span className="text-red-600">*</span>
          </label>
          <Textarea
            id="message"
            rows={5}
            error={Boolean(errors.message)}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? 'message-error' : undefined}
            {...register('message', { 
              required: 'Le message est requis',
              minLength: {
                value: 10,
                message: 'Le message doit contenir au moins 10 caractères'
              }
            })}
          />
          {errors.message && (
            <p id="message-error" className="mt-1 text-sm text-red-600">{errors.message.message}</p>
          )}
        </div>
        
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="acceptConditions"
              type="checkbox"
              className={`h-4 w-4 rounded-md border-white/20 bg-white/5 text-accent focus:ring-accent/20 ${errors.acceptConditions ? 'border-red-500' : ''}`}
              aria-invalid={Boolean(errors.acceptConditions)}
              aria-describedby={errors.acceptConditions ? 'conditions-error' : undefined}
              {...register('acceptConditions', { required: 'Vous devez accepter les conditions' })}
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="acceptConditions" className="font-medium text-white/80">
              J&apos;accepte que mes données soient utilisées pour traiter ma demande <span className="text-red-600">*</span>
            </label>
            {errors.acceptConditions && (
              <p id="conditions-error" className="mt-1 text-sm text-red-600">{errors.acceptConditions.message}</p>
            )}
          </div>
        </div>
        
        <div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
          </Button>
        </div>
      </form>
    </div>
  );
}
